import { InputHTMLAttributes, useState, useEffect, ChangeEvent } from 'react'
import { Box, Input, InputGroup, InputLeftElement } from '@chakra-ui/react'
import { FiSearch } from 'react-icons/fi'

type DebouncedInputProps = {
    value: string | number
    placeholder?: string
    onChange: (value: string | number) => void
    debounce?: number
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'>

/**
 * A debounced input component that triggers an onChange event after a specified delay.
 *
 * @param {object} props - The component props.
 * @param {string} props.value - The initial value of the input.
 * @param {Function} props.onChange - The function to call when the input value changes.
 * @param {number} props.debounce - The delay in milliseconds before the onChange event is triggered.
 * @returns {JSX.Element} - The debounced input component.
 */

export default function DebouncedInput({
    value: initialValue,
    onChange,
    debounce = 500,
    placeholder = 'Search...'
}: DebouncedInputProps) {
    const [value, setValue] = useState(initialValue)

    useEffect(() => {
        setValue(initialValue)
    }, [initialValue])

    useEffect(() => {
        const timeout = setTimeout(() => {
            onChange(value)
        }, debounce)

        return () => clearTimeout(timeout)
    }, [value, onChange, debounce])

    return (
        <Box py={6}>
            <InputGroup>
                <InputLeftElement pointerEvents="none">
                    <FiSearch />
                </InputLeftElement>
                <Input
                    w={'xs'}
                    focusBorderColor="brand.500"
                    borderColor="brand.100"
                    borderWidth="1px"
                    variant="filled"
                    placeholder={placeholder}
                    value={value}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setValue(e.target.value)
                    }
                    type="search"
                />
            </InputGroup>
        </Box>
    )
}
